import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useMobileViewport from './useMobileViewport';

/**
 * Keeps desktop content in its original layout, but moves an open mobile
 * overlay to document.body so host-header stacking contexts cannot cover it.
 */
const SportsbookMobilePortal = ({
  children,
  open,
  inactive = false,
  breakpoint,
  className = '',
}) => {
  const isMobile = useMobileViewport(breakpoint);
  const [entered, setEntered] = useState(false);
  const animationFrameRef = useRef({ first: null, second: null });

  useEffect(() => {
    const cancelFrames = () => {
      if (animationFrameRef.current.first != null) {
        window.cancelAnimationFrame(animationFrameRef.current.first);
      }
      if (animationFrameRef.current.second != null) {
        window.cancelAnimationFrame(animationFrameRef.current.second);
      }
      animationFrameRef.current = { first: null, second: null };
    };

    cancelFrames();
    if (!isMobile || !open) {
      if (typeof window !== 'undefined') {
        animationFrameRef.current.first = window.requestAnimationFrame(() => setEntered(false));
      }
      return cancelFrames;
    }

    animationFrameRef.current.first = window.requestAnimationFrame(() => {
      animationFrameRef.current.second = window.requestAnimationFrame(() => setEntered(true));
    });
    return cancelFrames;
  }, [isMobile, open]);

  if (!isMobile) return children;
  if (!open || typeof document === 'undefined') return null;

  const portalClassName = [
    'sportsbook-theme',
    'sportsbook-route-shell',
    'zxsb_mobile_portal_root',
    entered ? 'zxsb_mobile_portal_entered' : 'zxsb_mobile_portal_initial',
    className,
  ].filter(Boolean).join(' ');

  return createPortal(
    <div className={portalClassName} inert={inactive ? '' : undefined} aria-hidden={inactive || undefined}>{children}</div>,
    document.body,
  );
};

export default SportsbookMobilePortal;
